/**
 * Comprehensive Data Capture Utility for Support Tickets
 * Captures browser info, screenshots, performance metrics, and more
 */

export interface BrowserInfo {
  userAgent: string;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType: 'Desktop' | 'Mobile' | 'Tablet';
  screenResolution: string;
  viewport: string;
  timezone: string;
  language: string;
  colorDepth?: string;
  pixelRatio?: number;
  cookiesEnabled?: boolean;
  doNotTrack?: string;
}

export interface PerformanceMetrics {
  pageLoadTime?: number;
  domContentLoaded?: number;
  firstPaint?: number;
  memoryUsage?: number;
}

export interface NetworkInfo {
  onlineStatus: boolean;
  connectionType?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

export interface AdditionalContext {
  localStorage?: string;
  sessionStorage?: string;
  currentUrl?: string;
  referrer?: string;
  performanceMetrics?: PerformanceMetrics;
  networkInfo?: NetworkInfo;
  errors?: string[];
  [key: string]: string | PerformanceMetrics | NetworkInfo | string[] | undefined;
}

/**
 * Captures comprehensive browser information
 */
export const getBrowserInfo = (): BrowserInfo => {
  const ua = navigator.userAgent;

  // Detect browser
  let browser = 'Unknown';
  let browserVersion = '';

  if (ua.includes('Edg/')) {
    browser = 'Edge';
    browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] || '';
  } else if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
    browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] || '';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
    browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] || '';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
    browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] || '';
  }

  // Detect OS
  let os = 'Unknown';
  let osVersion = '';

  if (ua.includes('Windows NT')) {
    os = 'Windows';
    const ver = ua.match(/Windows NT ([\d.]+)/)?.[1];
    const versionMap: { [key: string]: string } = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
    };
    osVersion = versionMap[ver || ''] || ver || '';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    osVersion = ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
    osVersion = ua.match(/Android ([\d.]+)/)?.[1] || '';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '';
  }

  // Detect device type
  let deviceType: 'Desktop' | 'Mobile' | 'Tablet' = 'Desktop';
  if (/Mobile|Android|iPhone/.test(ua) && !ua.includes('iPad')) {
    deviceType = 'Mobile';
  } else if (/Tablet|iPad/.test(ua)) {
    deviceType = 'Tablet';
  }

  return {
    userAgent: ua,
    browser,
    browserVersion,
    os,
    osVersion,
    deviceType,
    screenResolution: `${screen.width}x${screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    colorDepth: `${screen.colorDepth}-bit`,
    pixelRatio: window.devicePixelRatio,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || 'not specified',
  };
};

/**
 * Captures current page screenshot using html2canvas
 */
export const captureScreenshot = async (): Promise<Blob | null> => {
  try {
    // Dynamically import html2canvas to avoid bundle size issues
    const html2canvas = (await import('html2canvas')).default;

    const canvas = await html2canvas(document.body, {
      allowTaint: true,
      useCORS: true,
      logging: false,
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      scrollX: 0,
      scrollY: 0,
    });

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.8 // 80% quality for reasonable file size
      );
    });
  } catch (error) {
    console.error('Failed to capture screenshot:', error);
    return null;
  }
};

/**
 * Captures performance metrics
 */
export const getPerformanceMetrics = (): PerformanceMetrics => {
  const metrics: PerformanceMetrics = {};

  try {
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      metrics.pageLoadTime = timing.loadEventEnd - timing.navigationStart;
      metrics.domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
      metrics.firstPaint = timing.responseEnd - timing.fetchStart;
    }

    // Memory usage (Chrome only)
    interface PerformanceMemory {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    }
    const perfWithMemory = performance as Performance & { memory?: PerformanceMemory };
    if (perfWithMemory.memory) {
      metrics.memoryUsage = Math.round(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024); // MB
    }
  } catch {
    console.error('Failed to capture performance metrics');
  }

  return metrics;
};

/**
 * Captures network information
 */
export const getNetworkInfo = (): NetworkInfo => {
  const info: NetworkInfo = {
    onlineStatus: navigator.onLine,
  };

  try {
    interface NetworkInformation {
      type?: string;
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    }
    type NavigatorWithConnection = Navigator & {
      connection?: NetworkInformation;
      mozConnection?: NetworkInformation;
      webkitConnection?: NetworkInformation;
    };
    const nav = navigator as NavigatorWithConnection;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

    if (connection) {
      info.connectionType = connection.type || 'unknown';
      info.effectiveType = connection.effectiveType || 'unknown';
      info.downlink = connection.downlink; // Mbps
      info.rtt = connection.rtt; // ms
    }
  } catch {
    console.error('Failed to capture network info');
  }

  return info;
};

/**
 * Captures additional context including localStorage, sessionStorage, etc.
 */
export const getAdditionalContext = (customContext?: Record<string, any>): AdditionalContext => {
  const context: AdditionalContext = {
    currentUrl: window.location.href,
    referrer: document.referrer || 'Direct',
    performanceMetrics: getPerformanceMetrics(),
    networkInfo: getNetworkInfo(),
  };

  // Capture localStorage size (not content for privacy)
  try {
    let localStorageSize = 0;
    for (const key in localStorage) {
      if (Object.hasOwn(localStorage, key)) {
        localStorageSize += localStorage[key].length + key.length;
      }
    }
    context.localStorage = `${(localStorageSize / 1024).toFixed(2)} KB`;
  } catch {
    context.localStorage = 'Unable to access';
  }

  // Capture sessionStorage size
  try {
    let sessionStorageSize = 0;
    for (const key in sessionStorage) {
      if (Object.hasOwn(sessionStorage, key)) {
        sessionStorageSize += sessionStorage[key].length + key.length;
      }
    }
    context.sessionStorage = `${(sessionStorageSize / 1024).toFixed(2)} KB`;
  } catch {
    context.sessionStorage = 'Unable to access';
  }

  // Merge custom context
  if (customContext) {
    Object.assign(context, customContext);
  }

  return context;
};

/**
 * Main function to capture all comprehensive data for ticket creation
 */
export const captureComprehensiveTicketData = async (
  customContext?: Record<string, any>,
  includeScreenshot: boolean = true
): Promise<{
  browserInfo: BrowserInfo;
  additionalContext: AdditionalContext;
  screenshot: Blob | null;
}> => {
  const browserInfo = getBrowserInfo();
  const additionalContext = getAdditionalContext(customContext);
  const screenshot = includeScreenshot ? await captureScreenshot() : null;

  return {
    browserInfo,
    additionalContext,
    screenshot,
  };
};

/**
 * Helper to convert error to stack trace string
 */
export const getErrorStack = (error: Error | any): string => {
  if (error instanceof Error && error.stack) {
    return error.stack;
  }
  return String(error);
};
